import { HashRouter, Link, Route, Switch } from "react-router-dom";
import ReportPage from "./page/ReportPage";
import DashboardPage from "./page/DashboardPage";
import HomePage from "./page/HomePage";
import ConfigPage from "./page/ConfigPage";
import SimulationPage from "./page/SimulationPage";
import BacktesterPage from "./page/BacktesterPage";
import ImportPage from "./page/ImportPage";
import AdminPage from "./page/AdminPage";
import { useEffect, useState } from "react";
import AuthService from "./service/AuthService";
import InactivityLogout from "./service/InactivityAuthService";
import OdometerConfigPage from "./page/OdometerConfigPage";
import HotspotConfigPage from "./page/HotspotConfigPage";
import VolumeAutoUpdateAdminPage from "./page/VolumeAutoUpdateAdminPage";
import AutoDeclineAdminPage from "./page/AutoDeclineAdminPage";
import NotePage from "./page/NotePage";
import VolumeHistoryConfigPage from "./page/VolumeHistoryConfigPage";
import AutoAllowConfigPage from "./page/AutoAllowConfigPage";
import AuditlogPage from "./page/AuditlogPage";
import ApiConnector from "./connector/ApiConnector";
import moment from "moment";
import ResultSimulationPage from "./page/ResultSimulationPage";
import DetailResultSimulationPage from "./page/DetailResultSimulationPage";
import SuccessStatisticsDashboardPage from "./page/SuccessStatisticsDashboardPage";
import VulnerablePanPage from "./page/VulnerablePanPage";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowsLeftRight } from "@fortawesome/free-solid-svg-icons/faArrowsLeftRight";
import { faCircleNotch } from "@fortawesome/free-solid-svg-icons/faCircleNotch";
import { faCircleCheck } from "@fortawesome/free-solid-svg-icons/faCircleCheck";
import "@fortawesome/fontawesome-free/css/all.css";
import CustomerHistoryPatternsPage from "./page/CustomerHistoryPatternsPage";
import { ExportProvider, useExport } from "./context/ExportContext";

function NavBar({ tokenPayload, isScreenWide, onToggleWidescreen }) {
  const { isExportActive, isExportDone } = useExport();

  return (
    <header>
      <nav className="noprint sticky">
        <ul className="nav">
          <li>
            <Link to="/">
              <img src="ewlogo.png" alt="Logo" />
            </Link>
          </li>
          <li>
            <Link to="/dashboard" className="nav-button">
              Dashboard
            </Link>
          </li>
          <li>
            <Link to="/report" className="nav-button">
              Report
            </Link>
          </li>
          <li>
            <Link to="/config" className="nav-button">
              Rules
            </Link>
          </li>
          <li>
            <Link to="/simulation" className="nav-button">
              Simulation
            </Link>
          </li>
          <li>
            <Link to="/backtester" className="nav-button">
              Backtester
            </Link>
          </li>
          <li>
            <Link to="/customer-history-pattern" className="nav-button">
              Patterns
            </Link>
          </li>
          <li>
            <Link to="/import" className="nav-button">
              Import
            </Link>
          </li>
          <li>
            <Link to="/admin" className="nav-button">
              Admin
            </Link>
          </li>
          {tokenPayload && (
            <li className="float-right sticky" style={{ paddingTop: "1.2rem" }}>
              {(isExportActive || isExportDone) && (
                <Link
                  to="/report?fromExport=1"
                  className="nav-button"
                  title={
                    isExportDone
                      ? "Export ready — click to view"
                      : "Export in progress…"
                  }
                  style={{ color: "#4caf50" }}
                >
                  <FontAwesomeIcon
                    icon={isExportDone ? faCircleCheck : faCircleNotch}
                    spin={isExportActive}
                  />{" "}
                  Export
                </Link>
              )}
              <a
                className="nav-button"
                onClick={() => onToggleWidescreen()}
                title="Make UI appear wider on screen."
              >
                <FontAwesomeIcon icon={faArrowsLeftRight} />
              </a>
              <a className="nav-button" onClick={() => AuthService.logout()}>
                Logout <small>({AuthService.getUserName()})</small>
              </a>
            </li>
          )}
        </ul>
      </nav>
    </header>
  );
}

function App() {
  const [tokenPayload, setTokenPayload] = useState(null);
  const [isScreenWide, setIsScreenWide] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    initializeMsalAndHandleAuth();
  }, []);

  useEffect(() => {
    if (isInitialized) {
      InactivityLogout.runLogoutOnInactivityBackgroundJob();
    }
  }, [isInitialized]);

  const initializeMsalAndHandleAuth = async () => {
    try {
      // Initialize MSAL and handle redirect response
      const response = await AuthService.initializeMsal();

      if (response) {
        // User just logged in via redirect

        // Set active account
        const msalInstance = AuthService.getMsalInstance();
        msalInstance.setActiveAccount(response.account);

        // Set session start time
        AuthService.setSessionStartTime(moment().toISOString());

        // Update token payload state
        setTokenPayload(AuthService.getTokenPayload());

        // Acquire access token for API calls (ensures token is cached)
        try {
          await AuthService.getAccessToken();
        } catch (error) {
          console.warn("Failed to acquire access token on login:", error);
        }

        // Log user login
        try {
          await ApiConnector.logUserLoginAuditlog();
        } catch (error) {
          console.warn("Audit log failed:", error);
        }

        // Mark as initialized
        setIsInitialized(true);
      } else {
        // No redirect response, check if user is already logged in
        const msalInstance = AuthService.getMsalInstance();
        const accounts = msalInstance.getAllAccounts();

        if (accounts.length > 0) {
          // User is already logged in
          msalInstance.setActiveAccount(accounts[0]);
          setTokenPayload(AuthService.getTokenPayload());

          // Ensure access token is available (acquireTokenSilent will use cache or refresh if needed)
          try {
            await AuthService.getAccessToken();
          } catch (error) {
            console.warn("Failed to verify access token:", error);
          }

          setIsInitialized(true);
        } else {
          // User is not logged in, redirect to login
          setIsInitialized(true);
          await AuthService.redirectToLoginPage();
        }
      }
    } catch (error) {
      console.error("MSAL initialization error:", error);
      alert("Authentication initialization failed: " + error.message);
      setIsInitialized(true);
    }
  };

  const toggleWidescreen = () => {
    const current = !isScreenWide;
    setIsScreenWide(current);
    let width = "";
    if (current) {
      width = "120rem";
    } else {
      width = "60rem";
    }
    document.documentElement.style.setProperty("--width", width);
  };

  return (
    <HashRouter>
      <ExportProvider>
        {tokenPayload && (
          <>
            <NavBar
              tokenPayload={tokenPayload}
              isScreenWide={isScreenWide}
              onToggleWidescreen={toggleWidescreen}
            />

            <Switch>
              {/* Pages - present in menu */}

              <Route exact path="/">
                <HomePage />
              </Route>
              <Route exact path="/dashboard">
                <DashboardPage />
              </Route>
              <Route exact path="/report">
                <ReportPage />
              </Route>
              <Route exact path="/customer-history-pattern">
                <CustomerHistoryPatternsPage />
              </Route>
              <Route exact path="/config">
                <ConfigPage />
              </Route>
              <Route exact path="/simulation">
                <SimulationPage />
              </Route>
              <Route exact path="/backtester">
                <BacktesterPage />
              </Route>
              <Route exact path="/import">
                <ImportPage />
              </Route>
              <Route exact path="/admin">
                <AdminPage />
              </Route>

              {/* Sub-pages - not present in menu */}

              <Route exact path="/admin/volume-auto-update">
                <VolumeAutoUpdateAdminPage />
              </Route>
              <Route exact path="/admin/auto-decline">
                <AutoDeclineAdminPage />
              </Route>
              <Route exact path="/auditlog">
                <AuditlogPage />
              </Route>
              <Route exact path="/config/odometer">
                <OdometerConfigPage />
              </Route>
              <Route exact path="/config/volume-history">
                <VolumeHistoryConfigPage />
              </Route>
              <Route exact path="/config/auto-allow">
                <AutoAllowConfigPage />
              </Route>
              <Route exact path="/config/hotspot">
                <HotspotConfigPage />
              </Route>
              <Route exact path="/note">
                <NotePage />
              </Route>
              <Route exact path="/simulation/result">
                <ResultSimulationPage />
              </Route>
              <Route exact path="/simulation/result/detail">
                <DetailResultSimulationPage />
              </Route>
              <Route exact path="/dashboard/success-statistics">
                <SuccessStatisticsDashboardPage />
              </Route>
              <Route exact path="/dashboard/vulnerable-pans">
                <VulnerablePanPage />
              </Route>
            </Switch>
          </>
        )}
      </ExportProvider>
    </HashRouter>
  );
}

export default App;
